import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartFunnelPageConfig } from '@front-platform/content-builder';
import { kioskCartFunnelPageResolveConfig } from '@front-platform/content-builder';
import { resolveCartFunnelPadding } from '@shared/blocks/kiosk/cart-funnel/kiosk-cart-funnel-top-padding';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostBinding,
  OnDestroy,
  input,
  untracked,
} from '@angular/core';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';

import { Paths } from '@config/paths.config';
import { IOnBehalfUser } from '@core/models/order.model';
import { DoodCartFunnelValueTarget, isCartFunnelStepComplete } from '@store/cart-funnel';

import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';

import { CartService } from '@core/services/cart/cart.service';
import { CartFunnelContinueService } from '@core/services/cart-funnel/cart-funnel-continue.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import {
  normalizeCartFunnelPageBody,
  planCartFunnelStepsSync,
} from '../../cart-funnel-page/cart-funnel-page.util';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

export interface KioskCartFunnelPageRuntimeConfig {
  'body'?: unknown[] | undefined;
  'bottom_content'?: unknown[] | undefined;
  'content'?: unknown[] | undefined;
  'header'?: unknown[] | undefined;
}

export type KioskCartFunnelPageConfigInput = Partial<
  Omit<KioskCartFunnelPageConfig, keyof KioskCartFunnelPageRuntimeConfig> &
    KioskCartFunnelPageRuntimeConfig
>;
export type KioskCartFunnelPageResolvedConfig = Omit<
  KioskCartFunnelPageConfig,
  keyof KioskCartFunnelPageRuntimeConfig
> &
  Partial<KioskCartFunnelPageRuntimeConfig>;
export interface KioskCartFunnelPageContract extends ConfigComponentContract<
  KioskCartFunnelPageConfigInput,
  KioskCartFunnelPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-funnel-page',
  templateUrl: './kiosk-cart-funnel-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KioskCartFunnelPageComponent implements OnDestroy, KioskCartFunnelPageContract {
  readonly config = input<KioskCartFunnelPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartFunnelPageConfig,
    KioskCartFunnelPageRuntimeConfig
  >(kioskCartFunnelPageResolveConfig, () => ({ config: this.config() }));
  private _destroyerRef = new Subject<boolean>();

  get body(): unknown[] | undefined {
    return this.resolvedConfig()['body'] as unknown as unknown[] | undefined;
  }
  get header(): unknown[] | undefined {
    return this.resolvedConfig()['header'] as unknown as unknown[] | undefined;
  }
  get content(): unknown[] | undefined {
    return this.resolvedConfig()['content'] as unknown as unknown[] | undefined;
  }
  get bottomContent(): unknown[] | undefined {
    return this.resolvedConfig()['bottom_content'] as unknown as unknown[] | undefined;
  }
  get bottomPadding(): string {
    return resolveCartFunnelPadding(this.resolvedConfig().bottom_padding);
  }
  protected readonly steps = computed(() => normalizeCartFunnelPageBody(this.body));

  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get backgroundImageCss(): string | null {
    const url = this.backgroundImage?.url;
    return url ? `url(${url})` : null;
  }
  get buttonsBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['buttons_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get validateButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['validate_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get validateButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['validate_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get cancelButtonBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['cancel_button_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get cancelButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['cancel_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  @HostBinding() class = 'relative flex h-screen flex-col overflow-hidden';

  protected readonly isValid$: Observable<boolean> = this.cartFunnelSelector.selectActiveStep.pipe(
    switchMap(active => {
      return this.cartFunnelSelector.selectValues.pipe(
        map(values => values.filter(v => v.formId === active?.id)),
      );
    }),
    map(values => isCartFunnelStepComplete(values) && values.length !== 0),
    debounceTime(200),
    distinctUntilChanged(),
    takeUntil(this._destroyerRef),
    startWith(false),
    shareReplay(1),
  );

  isLastItem = true;

  /** Signature of steps last projected into the cart-funnel store. */
  private lastSyncedStepIds: string | null = null;
  private displayableSkipWatchArmed = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly cartService: CartService,
    private basketSelector: BasketStoreSelector,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routerHelper: RouterHelperService,
    private cartFunnelSelector: CartFunnelStoreSelector,
    private cartFunnelDispatcher: CartFunnelStoreDispatcher,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly analyticsService: AnalyticsService,
    private readonly cb2PreviewState: Cb2PreviewStateService,
    private readonly cartFunnelContinue: CartFunnelContinueService,
  ) {
    this.cartFunnelContinue.requests$
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => this.submit());
    this.cartFunnelSelector.selectIsLastStepActive
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(_isLastItem => {
        this.isLastItem = _isLastItem;
        this.cdr.markForCheck();
      });

    // Re-project body steps whenever soft-refresh updates the draft (empty boot → form).
    effect(() => {
      const steps = this.steps();
      untracked(() => this.syncFunnelStepsFromBody(steps.map(step => step.block)));
    });
  }

  ngOnDestroy(): void {
    this.cartFunnelDispatcher.leaveCartFunnel();
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  private syncFunnelStepsFromBody(stepIds: readonly string[]): void {
    const plan = planCartFunnelStepsSync({
      stepIds,
      previouslySyncedKey: this.lastSyncedStepIds,
      isSharedOrder: !!this.basketSelector.basket.id,
      isPreview: this.cb2PreviewState.isActive(),
    });

    this.lastSyncedStepIds = plan.nextSyncedKey;

    switch (plan.action) {
      case 'noop':
        return;
      case 'skip':
        this.skipFunnel();
        return;
      case 'clear':
        this.cartFunnelDispatcher.leaveCartFunnel();
        this.cartFunnelDispatcher.reset();
        return;
      case 'sync':
        if (plan.resetFirst) {
          this.cartFunnelDispatcher.leaveCartFunnel();
          this.cartFunnelDispatcher.reset();
        }
        this.addBlocksToStore(plan.stepIds);
        this.ensureDisplayableSkipWatch();
        return;
    }
  }

  private addBlocksToStore(stepIds: readonly string[]): void {
    stepIds.forEach((id, index) => {
      this.cartFunnelDispatcher.addStep({ id, index });
    });
  }

  private ensureDisplayableSkipWatch(): void {
    if (this.displayableSkipWatchArmed) {
      return;
    }
    this.displayableSkipWatchArmed = true;

    this.cartFunnelSelector.selectDisplayableStepsIds
      .pipe(delay(1), takeUntil(this._destroyerRef))
      .subscribe(displayableStepsIds => {
        if (displayableStepsIds.length === 0) {
          this.skipFunnel();
        }
      });
  }

  private skipFunnel(): void {
    if (this.cb2PreviewState.isActive()) {
      return;
    }

    const navigationHistory = this.navigationHistoryService.getHistory();
    const isLastPagePayment =
      navigationHistory[navigationHistory.length - 1].identifier === 'payment';
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'cart-funnel',
      this.activatedRoute.snapshot,
    );

    if (isLastPagePayment) {
      this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Cart}`)]);
      return;
    }

    this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Payment}`)]);
  }

  submit(): void {
    combineLatest([
      this.cartFunnelSelector.selectNextStep,
      this.cartFunnelSelector.selectIsLastStepActive,
      this.cartFunnelSelector.selectActiveStep.pipe(
        switchMap(active =>
          this.cartFunnelSelector.selectValues.pipe(
            map(values => values.filter(value => value.formId === active?.id)),
          ),
        ),
      ),
    ])
      .pipe(
        take(1),
        map(([_next, _isLastStep, values]) => {
          if (!isCartFunnelStepComplete(values) || values.length === 0) {
            return;
          }
          if (_isLastStep) {
            this.goToPaymentPage();
          } else {
            if (_next) {
              this.cartFunnelDispatcher.setActive(_next.id);

              setTimeout(() => {
                const inputs = document.querySelectorAll('input');
                if (inputs[0]) {
                  inputs[0].focus();
                }
              }, 1);
            }
          }
        }),
        tap(() => this.useFormValues()),
      )
      .subscribe();
  }

  back(): void {
    combineLatest([
      this.cartFunnelSelector.selectDisplayableStepsIds,
      this.cartFunnelSelector.selectActiveStepId,
    ])
      .pipe(
        take(1),
        tap(([displayableStepsIds, activeStepId]) => {
          if (!activeStepId) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }
          const activeStepIndex = displayableStepsIds.indexOf(activeStepId);

          if (activeStepIndex === 0) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }

          if (activeStepIndex === -1) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }

          this.cartFunnelDispatcher.setActive(displayableStepsIds[activeStepIndex - 1]);
        }),
      )
      .subscribe();
  }

  private goToPaymentPage(): void {
    const isSharedOrderExist = this.basketSelector.basket.id;

    if (isSharedOrderExist) {
      this.router.navigate([
        this.routerHelper.translateRoute(`/${Paths.Validate}/${Paths.SharedOrder}`),
      ]);
    } else {
      this.router.navigate([this.routerHelper.translateRoute(`/${Paths.Payment}`)]);
    }
  }

  useFormValues(): void {
    this.cartFunnelSelector.selectValues
      .pipe(
        take(1),
        map(forms => {
          const orderCommentValues: string[] = [];
          let hasOnBehalfUserField = false;
          const onBehalfUser: IOnBehalfUser = {
            optin: false,
          };

          forms.forEach(form => {
            const target = form.target ?? DoodCartFunnelValueTarget.OrderComment;
            if (target === DoodCartFunnelValueTarget.OrderComment && form.value) {
              let title = null;

              if (form.displayCustomTitleInComment) {
                title = form.customTitle;
              } else if (form.displayTitle) {
                title = form.title;
              }
              let value = form.value;
              if (value === true) {
                value = 'oui';
              }

              orderCommentValues.push([title, value].filter(i => i).join(' : '));
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserFullName) {
              onBehalfUser.full_name = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserEmail) {
              onBehalfUser.email = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserPhone) {
              onBehalfUser.phone = form.value as string;
              hasOnBehalfUserField = true;
            }
            if (target === DoodCartFunnelValueTarget.OnBehalfUserOptin) {
              onBehalfUser.optin = form.value === true;
              hasOnBehalfUserField = true;
            }
          });

          this.cartService.setOnBehalfUser(hasOnBehalfUserField ? onBehalfUser : undefined);
          this.cartFunnelDispatcher.updateOrderComment(orderCommentValues.join(' ; '));
        }),
        switchMap(() => this.orderDispatcher.checkCartIsValid$()),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartFunnelPageComponent, { 'keyAliases': {} });
